import * as React from "react";
import { useState, useEffect } from "react";
import { store } from "../../redux/oldStore";
import Select, { SingleValue, StylesConfig } from "react-select";
import { toast } from "react-toastify";
import Icon, { IconType } from "../Icon/Icon";
import ExpandableDiv, { ExpandableImg, ExpandableVideo, LinkToView } from "../Tools/ExpandableDiv";
import '../Showcase/projectDetailsStyles.css';
import { ICustomQuestions, IProject, IShowcaseSettings, IAward, ITag } from "./ShowcaseTypes";
import TopEntryControl from "./TopEntryControl";

interface IProjectDetails2Props{
    project:IProject;
    settings:IShowcaseSettings;
    projectQuestions:ICustomQuestions[];
    handleTagChange?:Function;
    availableProjectTags?:ITag[];
    categoryAwardsDict?:{[key: number]: IAward};
    specialAwardsDict?:{[key: number]: IAward};
    myLikes: any;
    likeProject:Function;
    likes:number;
    myTopRank?: number;
    topEntryCount?: number;
    onTopEntryClick?: (projectId: number) => void;
    onPickTopRank?: (projectId: number, rank: number) => void;
}
export const imageExtTypeDic:Record<string,boolean> = {};
imageExtTypeDic[".jpg"] = true;
imageExtTypeDic[".jpeg"] = true;
imageExtTypeDic[".png"] = true;
imageExtTypeDic[".tiff"] = true;
imageExtTypeDic[".webp"] = true;
imageExtTypeDic[".heic"] = true;

const ownerInitials = (first?: string, last?: string) => {
  const a = (first || "").trim().charAt(0);
  const b = (last || "").trim().charAt(0);
  return `${a}${b}`.toUpperCase();
};

const hasHtmlText = (html?: string) =>
  !!html && html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").trim().length > 0;

export const ProjectDetails2 = (props:IProjectDetails2Props) => {
  const { project, settings, projectQuestions } = props;
  const imagesList = project.images ?? [];

  let images = imagesList.filter(x=> imageExtTypeDic[x.ext] && x.fileType == "project-pic");
  let videos = imagesList.filter(x=>x.ext === '.mp4' && x.fileType === "project-pic");
  let files = imagesList.filter(x=>x.ext === '.pdf' && x.fileType === "project-pic");
  let fileEntryVideo = imagesList.find(x=> x.fileType == "entry-video-upload" && settings.showVideo);
  let pitchVideo = imagesList.find(x=> x.fileType == "pitch-video" && settings.showPitchVideo);
  let fileSlides = imagesList.find(x=> x.fileType == "slides-upload" && settings.showSlides);
  let fileLogbook = imagesList.find(x=> x.fileType == "log-book" && settings.showLogbook);
  let writtenMaterials = imagesList.find(x=> x.fileType == "written-materials");
  let entryFile = imagesList.find(x=> x.fileType == "entryfile");
  let documentaryUpload = imagesList.find(x=> x.fileType == "DocUpload");
  let performanceUpload = imagesList.find(x=> x.fileType == "PerformanceUpload");
  let exhibitPhoto = imagesList.find(x=> x.fileType == "NHD-ExhibitPhoto" && imageExtTypeDic[x.ext]);

  const visualImages = exhibitPhoto && !images.some((x) => x.key && x.key === exhibitPhoto.key)
    ? [...images, exhibitPhoto]
    : images;
  const heroImage = visualImages[0];
  const galleryImages = visualImages.slice(1);

  const [thisTags, setThisTags] = useState<ITag[]>([]);
  const [showAddTag, setShowAddTag] = useState(false);
  const [selectedTag, setSelectedTag] = useState({
    label: "Choose a Tag",
    value: "",
  });
  const [likes, setLikes] = useState<number>(props.likes);

  const customStyles: StylesConfig<{label: string; value: string}> = {
    menu: (provided) => ({
      ...provided,
      maxHeight: "20vh",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "20vh",
    }),
  };

  useEffect(() => {
      if(props.availableProjectTags){
    if (project.myTags) {
      let theseTags = props.availableProjectTags.filter((tag) =>
        project.myTags.includes(tag.Id)
      );
      setThisTags(theseTags);
    }
    }
  }, [project]);

  const mappedProjectTags = [
    { label: "Choose a Tag", value: "" },
    ...(props.availableProjectTags ?? [])
      .filter((tag) => !project.myTags?.includes(tag.Id))
      .map((tag) => {
        return {
          label: tag.Value,
          value: tag.Id,
        };
      }),
  ];

  const handleSaveThis = () => {
    if (!selectedTag?.value) {
      toast.error("need to choose a tag");
      return;
    }
    let reqBody = {
      id: project.publicId, //projectGuid
      tagId: selectedTag.value,
      remove: false,
    };
    store.server
      .postApi("../Tag/TagIt", reqBody)
      .then((res) => {
        console.log(res);
        if(props.handleTagChange) props.handleTagChange(project, selectedTag, false);
               
        setSelectedTag({ label: "Choose a Tag", value: "" });
        setShowAddTag(false);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleRemoveTag = (tag) => {
    let reqBody = {
      id: project.publicId,
      tagId: tag.Id,
      remove: true,
    };
    store.server
      .postApi("../Tag/TagIt", reqBody)
      .then((res) => {
        console.log(res);
        if(props.handleTagChange) props.handleTagChange(project, tag, true);
        setSelectedTag({ label: "Choose a Tag", value: "" });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleLike = (e)=>{
    const value = props.myLikes[project.projectIntId];
    var like = true;
    if(value === true || value === "true")
    like = false;
    props.myLikes[project.projectIntId] = like;
    
    if(like) setLikes(likes+1);
    else setLikes(likes- 1);

    props.likeProject(project.projectIntId, like);
    e.stopPropagation();
  };

  const showNames = !settings.hideShowcaseStudentName;
  const showPhotos = !settings.hideShowcaseStudentProfileImages;
  const owners = project.owners ?? [];
  const showTeam = owners.some((o) =>
    (showNames && (o.firstName || o.lastName)) ||
    o.School ||
    (showPhotos && (o.profilePath || o.profileKey))
  );

  const categoryAwards = props.settings.showAwards && props.categoryAwardsDict
    ? (project.categoryAwards ?? []).filter((x) => props.categoryAwardsDict[x])
    : [];
  const specialAwards = props.settings.showAwards && props.specialAwardsDict
    ? (project.specialAwards ?? []).filter((x) => props.specialAwardsDict[x])
    : [];

  const answeredQuestions = (projectQuestions ?? [])
    .map((x) => {
      const response = project.projectQuestionsResponses?.find(
        (r) => r.QuestionId === x.id
      );
      if (!response?.Value) return null;
      return { question: x.question, value: response.Value };
    })
    .filter((row): row is { question: string; value: string } => !!row);

  const hasAbstract = hasHtmlText(project.projectAbstract);
  const hasPlan = hasHtmlText(project.projectPlan);
  const hasDescription = !!(project.projectDescription && project.projectDescription.trim());
  const fileChips: { key: string; kind: string; node: React.ReactNode }[] = [];

  if (fileEntryVideo) {
    fileChips.push({
      key: "entry-video",
      kind: "vid",
      node: <LinkToView type="video" urlSource={fileEntryVideo.path} name="Entry video" />,
    });
  }
  if (pitchVideo) {
    fileChips.push({
      key: "pitch-video",
      kind: "vid",
      node: <LinkToView type="video" urlSource={pitchVideo.path} name="Pitch video" />,
    });
  }
  if (fileLogbook) {
    fileChips.push({
      key: "logbook",
      kind: "pdf",
      node: <LinkToView type="pdf" urlSource={fileLogbook.path} name="Logbook" />,
    });
  }
  if (fileSlides) {
    fileChips.push({
      key: "slides",
      kind: "pdf",
      node: <LinkToView type="pdf" urlSource={fileSlides.path} name="Slides" />,
    });
  }
  if (project.WrittenMatterials && writtenMaterials) {
    fileChips.push({
      key: "written",
      kind: "pdf",
      node: <LinkToView type="pdf" urlSource={writtenMaterials.path} name="Written materials" />,
    });
  }
  if (project.EntryFile && entryFile) {
    fileChips.push({
      key: "entry-file",
      kind: "pdf",
      node: <LinkToView type="pdf" urlSource={entryFile.path} name="Entry file" />,
    });
  }
  if (documentaryUpload) {
    fileChips.push({
      key: "doc",
      kind: "vid",
      node: <LinkToView type="video" urlSource={documentaryUpload.path} name="Documentary" />,
    });
  }
  if (performanceUpload) {
    fileChips.push({
      key: "perf",
      kind: "vid",
      node: <LinkToView type="video" urlSource={performanceUpload.path} name="Performance" />,
    });
  }
  files.forEach((x, i) => {
    fileChips.push({
      key: `pdf-${x.key || i}`,
      kind: "pdf",
      node: <LinkToView type="pdf" urlSource={x.path} name={x.comments || "Project file"} />,
    });
  });
  (project.files ?? []).forEach((x) => {
    fileChips.push({
      key: `file-${x.url}`,
      kind: "pdf",
      node: <LinkToView type="pdf" urlSource={x.url} name={x.name} />,
    });
  });
  if (project.videoUrl) {
    fileChips.push({
      key: "video-url",
      kind: "link",
      node: (
        <a href={project.videoUrl} target="_blank" rel="noopener noreferrer">
          <span><Icon type={IconType.link} /> Video</span>
          <small>{project.videoUrl}</small>
        </a>
      ),
    });
  }
  if (project.videoUrl2) {
    fileChips.push({
      key: "video-url-2",
      kind: "link",
      node: (
        <a href={project.videoUrl2} target="_blank" rel="noopener noreferrer">
          <span><Icon type={IconType.link} /> Video</span>
          <small>{project.videoUrl2}</small>
        </a>
      ),
    });
  }
  if (project.EntryUrl) {
    const site = `${project.EntryUrl}.NHDWebCentral.org`;
    fileChips.push({
      key: "website",
      kind: "link",
      node: (
        <a href={`https://${site}`} target="_blank" rel="noopener noreferrer">
          <span><Icon type={IconType.link} /> Website</span>
          <small>{site}</small>
        </a>
      ),
    });
  }
  (project.formResponses ?? []).forEach((formResp, i) => {
    fileChips.push({
      key: `form-${formResp.ResponsePublicId || i}`,
      kind: "form",
      node: (
        <LinkToView
          type="custom-forms"
          urlSource={formResp.ResponsePublicId}
          name={formResp.FormName || `Form response ${i + 1}`}
        />
      ),
    });
  });

  const hasGallery = galleryImages.length > 0 || videos.length > 0;
  const hasMedia = fileChips.length > 0 || hasGallery;
  const hasStory = hasAbstract || hasPlan;
  const liked = props.myLikes[project.projectIntId] === true || props.myLikes[project.projectIntId] === "true";

  const voteControls = (
    <div className="showcase-vote-controls">
      {settings.recordLikes && (
        <div className="like-container">
          <span className={`click pd-like${liked ? " on" : ""}`} onClick={handleLike}>
            {likes > 0 && <>{likes} </>}
            {liked ? <i className="fad fa-thumbs-up"></i> : <i className="fal fa-thumbs-up"></i>}
          </span>
        </div>
      )}
      <TopEntryControl
        projectId={project.projectIntId}
        rank={props.myTopRank}
        count={props.topEntryCount}
        showCount={!!settings.showTopEntryCounts && !settings.canSelectTopEntries}
        canSelect={!!settings.canSelectTopEntries}
        max={settings.topEntriesMax}
        variant="ranks"
        onClick={props.onTopEntryClick || (() => {})}
        onPickRank={props.onPickTopRank}
      />
      {!!settings.showTopEntryCounts && !!settings.canSelectTopEntries && (props.topEntryCount || 0) > 0 && (
        <span className="pd-top-count">{props.topEntryCount} people picked this</span>
      )}
    </div>
  );

  return (
    <div
      className="showcase-project details"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >

{props.availableProjectTags && props.availableProjectTags.length > 0 && (
        <div className="pd-staff-tags">
          <div>
            <div className="flex-between">
            <div className="flex wrap ">
              {thisTags.map((tag, i) => {
                return (
                  <div key={`${tag.Id}-${i}`} className="nice-tag flex">
                    <Icon type={IconType.tag}/> {tag.Value}
                    <span
                      onClick={() => handleRemoveTag(tag)}
                      className="click"
                    >
                      <Icon type={IconType.trashO}/>
                    </span>
                  </div>
                );
              })}
            </div>
            <button type="button" className="btn btn-xs btn-default"
              onClick={()=>{setShowAddTag(!showAddTag);}}
            >
              <Icon type={IconType.tags} /> Tag
            </button>
            </div>

            {showAddTag && 
            <div className="box tag-it">
              <div className="form-horizontal">
                <div className="form-group">
                  <label htmlFor="pd-tag" className="control-label col-sm-5">Tag</label>
                  <div className="col-sm-4">
                      <Select
                        onChange={(opt: SingleValue<{label: string; value: string}>) => { if(opt) setSelectedTag(opt); }}
                        value={selectedTag}
                        options={mappedProjectTags.map((x) => {return{label:x.label, value:x.value+''}})}
                        styles={customStyles}
                        placeholder={"choose tag"}
                        className=""
                      />
                  </div>
                </div>
                <div className="form-group">
                  <div className="col-sm-4 col-sm-offset-5">
                    <button
                      onClick={handleSaveThis}
                      className="btn btn-secondary"
                    >
                      <Icon type={IconType.tag} /> Tag
                    </button>
                    <button
                      onClick={()=>{setShowAddTag(false)}}
                      className="btn btn-default"
                    >
                      <Icon type={IconType.close} /> Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
            }
          </div>
        </div>
      )}

      <div className={`pd-hero${heroImage ? "" : " no-photo"}`}>
        {heroImage && (
          <div className="pd-hero-photo">
            <ExpandableImg imageSrc={[{ path: heroImage.path, alt: heroImage.comments || "" }]} />
          </div>
        )}
        <div className="pd-hero-meta">
          <div className="pd-chip-row">
            {project.projectId && <span className="pd-code">{project.projectId}</span>}
            {project.category && <span className="pd-chip pd-chip-cat">{project.category}</span>}
          </div>
          <h2 className="title">{project.title}</h2>
          {settings?.showShortTitle && project.shortTitle ? (
            <div className="pd-short-title">{project.shortTitle}</div>
          ) : null}
          {hasDescription && <p className="pd-desc">{project.projectDescription}</p>}
          {(categoryAwards.length > 0 || specialAwards.length > 0) && (
            <div className="pd-chip-row pd-awards">
              {categoryAwards.map((num, i) => (
                <span
                  className="pd-chip pd-chip-award"
                  key={`${props.categoryAwardsDict[num].Id}-c-${i}`}
                  title={props.categoryAwardsDict[num].Name}
                >
                  <i className="fas fa-award"></i> {props.categoryAwardsDict[num].Name}
                </span>
              ))}
              {specialAwards.map((num, i) => (
                <span
                  className="pd-chip pd-chip-special"
                  key={`${props.specialAwardsDict[num].Id}-s-${i}`}
                  title={props.specialAwardsDict[num].Name}
                >
                  <i className="fas fa-award"></i> {props.specialAwardsDict[num].Name}
                </span>
              ))}
            </div>
          )}
          {voteControls}
        </div>
      </div>

      {showTeam && (
        <div className="pd-section">
          <h2 className="pd-section-title">{owners.length > 1 ? "Team" : "Student"}</h2>
          <div className="pd-team-row">
            {owners.map((x, i) => {
              const name = showNames ? `${x.firstName || ""} ${x.lastName || ""}`.trim() : "";
              const photoPath = showPhotos && (x.profilePath || x.profileKey) ? x.profilePath : "";
              const initials = ownerInitials(x.firstName, x.lastName);
              if (!name && !x.School && !photoPath && !initials) return null;
              return (
                <div className="pd-team-card" key={`project-owner-${i}`}>
                  <div className={`pd-avatar${photoPath ? "" : " initials"}`}>
                    {photoPath ? (
                      <ExpandableImg
                        imageSrc={[{ path: photoPath, alt: "", type: "person" }]}
                      />
                    ) : initials ? (
                      initials
                    ) : (
                      <Icon type={IconType.userLight} />
                    )}
                  </div>
                  <div>
                    {name && <strong className="pd-team-name">{name}</strong>}
                    {x.School && <div className="pd-team-school">{x.School}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(hasStory || hasMedia) && (
        <div className={`pd-split${hasStory && hasMedia ? "" : " single"}`}>
          {hasStory && (
            <div className="pd-story">
              {hasAbstract && (
                <div className="pd-card pd-abstract">
                  <h2 className="pd-section-title">{settings?.text?.abstractLabel ?? "Abstract"}</h2>
                  <ExpandableDiv>
                    <div dangerouslySetInnerHTML={{ __html: project.projectAbstract }} />
                  </ExpandableDiv>
                </div>
              )}
              {hasPlan && (
                <div className="pd-card">
                  <h2 className="pd-section-title">Plan</h2>
                  <ExpandableDiv>
                    <div dangerouslySetInnerHTML={{ __html: project.projectPlan }} />
                  </ExpandableDiv>
                </div>
              )}
            </div>
          )}
          {hasMedia && (
            <div className="pd-card pd-media">
              <h2 className="pd-section-title">Media &amp; files</h2>
              {fileChips.length > 0 && (
                <div className="pd-chip-row pd-files">
                  {fileChips.map((chip) => (
                    <div className={`pd-file-chip pd-file-chip-${chip.kind}`} key={chip.key}>
                      {chip.node}
                    </div>
                  ))}
                </div>
              )}
              {hasGallery && (
                <div className="pd-gallery">
                  {galleryImages.filter((x) => x.path).map((x, i) => (
                    <div className="pd-gallery-item" key={`pd-image-${x.key || i}`}>
                      <ExpandableImg imageSrc={[{ path: x.path, alt: x.comments || "" }]} />
                    </div>
                  ))}
                  {videos.filter((x) => x.path).map((x, i) => (
                    <div className="pd-gallery-item" key={`pd-video-${x.key || i}`}>
                      <ExpandableVideo videoSrc={[{ path: x.path, alt: x.comments || "" }]} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {answeredQuestions.length > 0 && (
        <div className="pd-card pd-questions">
          <h2 className="pd-section-title">Entry questions</h2>
          <ExpandableDiv>
            <div className="pd-qa">
              {answeredQuestions.map((row, i) => {
                const isLink = row.value.toUpperCase().indexOf("HTTP") === 0;
                return (
                  <div key={`pq-${i}`}>
                    <div className="pd-qa-q">{row.question}</div>
                    <div className="pd-qa-a">
                      {isLink ? (
                        <a href={row.value} target="_blank" rel="noopener noreferrer">
                          {row.value}
                        </a>
                      ) : (
                        row.value
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ExpandableDiv>
        </div>
      )}

    </div>
  );
}
